import React, { useEffect, useState, useRef } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { api, getAuthToken, setAuthToken, mediaUrl } from '@/lib/api';
import { ADMIN } from '@/constants/testIds';
import { motion, AnimatePresence } from 'framer-motion';
import { Trash2, Upload, Plus, LogOut, X, Save, Menu } from 'lucide-react';
import ChannelsManager from '@/components/admin/ChannelsManager';
import MarketingManager from '@/components/admin/MarketingManager';
import SettingsManager from '@/components/admin/SettingsManager';
import CommandIntelligence from '@/components/admin/CommandIntelligence';
import MeetGManager from '@/components/admin/MeetGManager';
import CustomersManager from '@/components/admin/CustomersManager';
import IntegrationCenter from '@/components/admin/IntegrationCenter';
import OrdersManager from '@/components/admin/OrdersManager';
import CheckoutSettingsManager from '@/components/admin/CheckoutSettingsManager';
import DiscountCodesManager from '@/components/admin/DiscountCodesManager';
import CompanyInfoManager from '@/components/admin/CompanyInfoManager';
import LegalDocumentsManager from '@/components/admin/LegalDocumentsManager';
import FooterManager from '@/components/admin/FooterManager';
import DiscoveryPanel from '@/components/admin/DiscoveryPanel';
import ExperienceManager from '@/components/admin/ExperienceManager';
import TemplateAssignmentControl from '@/components/admin/TemplateAssignmentControl';
import SuggestionRulesManager from '@/components/admin/SuggestionRulesManager';
import BehavioralInsights from '@/components/admin/BehavioralInsights';
import { PublishStatusControl, PublishStatusBadge, PublishStatusTabs } from '@/components/admin/shared';
import RichTextEditor from '@/components/admin/RichTextEditor';
import ContentTypePicker, { CONTENT_TYPES } from '@/components/admin/ContentTypePicker';
import MixedChannelAdmin from '@/components/admin/MixedChannelAdmin';
import PrintfulCatalogPicker from '@/components/admin/PrintfulCatalogPicker';
import ProductGalleryEditor from '@/components/admin/ProductGalleryEditor';

// Non-channel admin tabs (channel tabs are generated dynamically from
// /api/admin/channels — see AdminDashboardPage). Keeping system tabs static
// preserves the existing admin structure verbatim.
const STATIC_TABS = [
  { id: 'home', label: 'Homepage / Daily Drop', group: 'Content' },
  { id: 'meet_g', label: 'Meet G', group: 'Content' },
  { id: 'channels', label: 'Channels & Nav', group: 'Structure' },
  { id: 'world', label: 'World Engine', group: 'Structure' },
  { id: 'products', label: 'Vault (All Products)', group: 'Channels' },
  { id: 'orders', label: 'Orders', group: 'Commerce' },
  { id: 'checkout', label: 'Checkout Settings', group: 'Commerce' },
  { id: 'discount_codes', label: 'Discount Codes', group: 'Commerce' },
  { id: 'marketing', label: 'Marketing', group: 'Reach' },
  { id: 'collab', label: 'Collab', group: 'Reach' },
  { id: 'subscribers', label: 'Letters', group: 'Reach' },
  { id: 'customers', label: 'Customers (CRM)', group: 'Reach' },
  { id: 'intelligence', label: 'Command Intelligence', group: 'Structure' },
  { id: 'integrations', label: 'Integration Center', group: 'Structure' },
  { id: 'settings', label: 'Site Settings', group: 'Structure' },
  { id: 'company_info', label: 'Company Information', group: 'Site' },
  { id: 'legal', label: 'Legal Documents', group: 'Site' },
  { id: 'footer', label: 'Global Footer', group: 'Site' },
  { id: 'experience', label: 'Experience Manager', group: 'Site' },
  { id: 'suggestions', label: 'Suggestion Engine', group: 'Site' },
  { id: 'behavioral', label: 'Behavioral Insights', group: 'Site' },
];

// Legacy channel keys that map to an existing dedicated CrudAdmin tab.
// When a channel with one of these keys exists in the DB, its dynamic tab
// re-uses the corresponding legacy tab id (music/videos/articles/podcasts)
// so all historical content and endpoints keep working untouched.
const LEGACY_CHANNEL_TAB_ID = {
  music: 'music',
  podcasts: 'podcasts',
  watch: 'videos',
  on_my_mind: 'articles',
};

const GROUP_ORDER = ['Structure', 'Content', 'Channels', 'Commerce', 'Reach', 'Site'];

async function uploadFile(kind, file) {
  const fd = new FormData();
  fd.append('file', file);
  const { data } = await api.post(`/admin/uploads/${kind}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  return { url: data.url, original_filename: data.original_filename || null };
}

function FileField({ label, kind, value, onChange, accept }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const { url, original_filename } = await uploadFile(kind, f);
      // Second argument carries upload metadata (e.g. original_filename) for
      // callers that need it (e.g. digital product download field). Callers
      // that only care about the URL can safely ignore it.
      onChange(url, { original_filename });
    } catch (err) {
      alert(err?.response?.data?.detail || 'Upload failed');
    } finally { setUploading(false); }
  };
  return (
    <div>
      <label className="text-xs font-mono tracking-widest text-[var(--pg-ink-muted)]">{label}</label>
      <div className="mt-1 flex gap-2 items-center">
        <input value={value || ''} onChange={(e) => onChange(e.target.value)} placeholder="URL or upload…" className="flex-1 pg-input rounded-[var(--pg-radius-sm)] px-3 py-2 text-sm" />
        <input ref={inputRef} type="file" onChange={onFile} accept={accept} className="hidden" />
        <button data-testid={ADMIN.uploadButton} type="button" onClick={() => inputRef.current?.click()} disabled={uploading} className="pg-btn-ghost rounded-[var(--pg-radius-sm)] px-3 py-2 text-xs flex items-center gap-2">
          <Upload size={14} /> {uploading ? '…' : 'Upload'}
        </button>
      </div>
      {value && <p className="mt-1 text-[11px] text-[var(--pg-ink-faint)] font-mono break-all">{value}</p>}
    </div>
  );
}

function TextField({ label, value, onChange, type = 'text', textarea, rows = 3, placeholder }) {
  return (
    <div>
      <label className="text-xs font-mono tracking-widest text-[var(--pg-ink-muted)]">{label}</label>
      {textarea ? (
        <textarea
          rows={rows} value={value || ''} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
          className="mt-1 w-full pg-input rounded-[var(--pg-radius-sm)] px-3 py-2 text-sm leading-relaxed"
        />
      ) : (
        <input
          type={type} value={value ?? ''} onChange={(e) => onChange(type === 'number' ? Number(e.target.value) : e.target.value)} placeholder={placeholder}
          className="mt-1 w-full pg-input rounded-[var(--pg-radius-sm)] px-3 py-2 text-sm"
        />
      )}
    </div>
  );
}

function Toggle({ label, value, onChange }) {
  return (
    <label className="flex items-center justify-between gap-2 px-3 py-2 pg-input rounded-[var(--pg-radius-sm)] cursor-pointer">
      <span className="text-xs font-mono tracking-widest text-[var(--pg-ink-muted)]">{label}</span>
      <input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} className="accent-[var(--pg-emerald)]" />
    </label>
  );
}

function TagsField({ label, value, onChange }) {
  const str = Array.isArray(value) ? value.join(', ') : '';
  return (
    <TextField
      label={label}
      value={str}
      onChange={(s) => onChange((s || '').split(',').map((t) => t.trim()).filter(Boolean))}
      placeholder="comma, separated, tags"
    />
  );
}

function Drawer({ open, onClose, title, children, onSave, saving }) {
  return (
    <AnimatePresence>
      {open && (
        <motion.div initial={{ opacity: 1 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 flex justify-end" onClick={onClose}>
          <div className="absolute inset-0 bg-[rgba(7,11,20,0.7)] backdrop-blur-sm" />
          <motion.div
            initial={{ x: 50, opacity: 0 }} animate={{ x: 0, opacity: 1 }} exit={{ x: 50, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.16,1,0.3,1] }}
            className="relative w-full max-w-xl h-full bg-[var(--pg-navy-900)] border-l border-[var(--pg-border-2)] overflow-y-auto pg-scroll"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-5 border-b border-[var(--pg-border-2)] sticky top-0 bg-[var(--pg-navy-900)] flex items-center justify-between z-10">
              <h3 className="font-serif text-xl text-[var(--pg-ink)]">{title}</h3>
              <button onClick={onClose} aria-label="close" className="p-2 text-[var(--pg-ink-muted)] hover:text-[var(--pg-ink)]"><X size={18} /></button>
            </div>
            <div className="p-5 space-y-4">{children}</div>
            <div className="p-5 border-t border-[var(--pg-border-2)] sticky bottom-0 bg-[var(--pg-navy-900)]">
              <button data-testid={ADMIN.saveButton} onClick={onSave} disabled={saving} className="w-full pg-btn-primary rounded-[var(--pg-radius-md)] py-3 text-sm flex items-center justify-center gap-2 disabled:opacity-60">
                <Save size={14} /> {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

const defaults = {
  audio: { title: '', description: '', kind: 'music', source_type: 'upload', file_url: '', embed_url: '', duration: '', cover_url: '', tags: [], published: true, featured: false, publish_status: 'published' },
  audio_music: { title: '', description: '', kind: 'music', source_type: 'upload', file_url: '', embed_url: '', duration: '', cover_url: '', tags: [], published: true, featured: false, publish_status: 'published' },
  audio_podcast: { title: '', description: '', kind: 'podcast', source_type: 'upload', file_url: '', embed_url: '', duration: '', cover_url: '', tags: [], published: true, featured: false, publish_status: 'published' },
  articles: { title: '', subtitle: '', slug: '', body: '', excerpt: '', cover_url: '', tags: [], read_time: '', published: true, featured: false, publish_status: 'published' },
  gallery: { title: '', caption: '', image_url: '', tags: [], published: true, publish_status: 'published' },
  videos: { title: '', description: '', embed_url: '', poster_url: '', tags: [], published: true, featured: false, publish_status: 'published' },
  products: { title: '', subtitle: '', description: '', price: 9.99, currency: 'usd', is_free: false, cover_url: '', download_url: '', download_filename: '', file_format: 'PDF', tags: [], published: true, featured: false, publish_status: 'published', fulfillment_provider: 'manual', printful_sync_variant_id: '', printful_sync_product_id: '', is_subscription: false, subscription_interval: 'month', content_type: 'product', channel_key: null, inventory_tracking: false, inventory_quantity: 0, variants: [], gallery_images: [], user_gallery_images: [], gallery_order: [], primary_image_url: '' },
  projects: { title: '', description: '', status: 'in_progress', progress: 0, cover_url: '', tags: [], published: true, publish_status: 'published' },
  home: { kind: 'daily_drop', title: '', body: '' },
};

// -----------------------------------------------------------------------------
// Universal Content Creation — content-type registry (Phase 13).
// Maps a picker key → { endpoint, defaults, renderForm, labelOne }.
// Used by CrudAdmin when a non-native content type is picked inside a channel.
// Function references below (AudioForm, ArticleForm, VideoForm, ProductForm,
// GalleryForm, ExternalLinkForm) are hoisted function declarations.
// -----------------------------------------------------------------------------
const CONTENT_TYPE_CONFIG = {
  audio: {
    endpoint: 'audio',
    defaults: defaults.audio,
    renderForm: (f, s) => AudioForm(f, s),
    labelOne: 'audio track',
  },
  video: {
    endpoint: 'videos',
    defaults: defaults.videos,
    renderForm: (f, s) => VideoForm(f, s),
    labelOne: 'video',
  },
  article: {
    endpoint: 'articles',
    defaults: { ...defaults.articles, content_type: 'article' },
    renderForm: (f, s) => ArticleForm(f, s),
    labelOne: 'article',
  },
  standard: {
    endpoint: 'articles',
    defaults: { ...defaults.articles, content_type: 'standard', read_time: '' },
    renderForm: (f, s) => ArticleForm(f, s),
    labelOne: 'standard content',
  },
  external_link: {
    endpoint: 'articles',
    defaults: {
      title: '', subtitle: '', slug: '', body: '', excerpt: '',
      cover_url: '', tags: [], read_time: '',
      published: true, featured: false, publish_status: 'published',
      content_type: 'external_link', external_url: '',
    },
    renderForm: (f, s) => ExternalLinkForm(f, s),
    labelOne: 'external link',
  },
  gallery: {
    endpoint: 'gallery',
    defaults: defaults.gallery,
    renderForm: (f, s) => GalleryForm(f, s),
    labelOne: 'gallery item',
  },
  product: {
    endpoint: 'products',
    defaults: { ...defaults.products, content_type: 'product' },
    renderForm: (f, s) => ProductForm(f, s),
    labelOne: 'product',
  },
  digital_download: {
    endpoint: 'products',
    defaults: {
      ...defaults.products,
      content_type: 'digital_download',
      fulfillment_provider: 'digital',
    },
    renderForm: (f, s) => ProductForm(f, s),
    labelOne: 'digital download',
  },
};

export default function AdminDashboardPage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState('home');
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [channels, setChannels] = useState([]); // dynamic from /api/admin/channels
  const [channelsLoading, setChannelsLoading] = useState(true);

  // Fetch channel list from the DB. The Channels group of the sidebar is
  // generated dynamically from this response. When admins create new channels
  // via the "Channels & Nav" tab, they show up here automatically.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (!getAuthToken()) return;
        const { data } = await api.get('/admin/channels');
        if (!cancelled) setChannels(Array.isArray(data) ? data : []);
      } catch (e) {
        if (!cancelled) setChannels([]);
      } finally {
        if (!cancelled) setChannelsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (!getAuthToken()) return <Navigate to="/admin/login" replace />;

  const logout = () => { setAuthToken(null); navigate('/admin/login'); };

  // Build the full tab set: static tabs + dynamic channel tabs.
  // - Channels with a legacy dedicated tab (music/podcasts/watch/on_my_mind)
  //   re-use their existing tab id so historical content endpoints keep working.
  // - Any other channel (user-created) gets a `channel__{key}` tab that renders
  //   MixedChannelAdmin, which aggregates every content type by channel_key.
  const dynamicChannelTabs = channels.map((c) => {
    const legacyId = LEGACY_CHANNEL_TAB_ID[c.key];
    return {
      id: legacyId || `channel__${c.key}`,
      label: c.label || c.key,
      group: 'Channels',
      channelKey: c.key,
      channelLabel: c.label || c.key,
      isDynamic: !legacyId,
    };
  });

  const TABS = [...STATIC_TABS, ...dynamicChannelTabs];
  const groupedTabs = GROUP_ORDER.map((g) => ({
    group: g,
    items: TABS.filter((t) => t.group === g),
  }));

  const currentTab = TABS.find((t) => t.id === tab) || TABS[0];

  return (
    <div className="min-h-[80vh] max-w-[1500px] mx-auto px-3 sm:px-6 lg:px-8 py-6 lg:py-10">
      <div className="pg-admin-page p-4 sm:p-5 lg:p-6">
      <div className="pg-admin-bar mb-6 lg:mb-8 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={() => setMobileNavOpen((o) => !o)}
            className="lg:hidden p-2 rounded-md border border-[var(--pg-border-2)] text-[var(--pg-ink-muted)] bg-black/30"
            aria-label="Toggle module list"
            data-testid="admin-mobile-nav-toggle"
          >
            <Menu size={16} />
          </button>
          <div>
            <p className="pg-label">CARETAKER COMMAND CENTER</p>
            <h1 className="font-serif text-2xl sm:text-3xl lg:text-4xl mt-1 text-[var(--pg-ink)]">The Back Room</h1>
          </div>
        </div>
        <button onClick={logout} className="pg-btn-ghost rounded-[var(--pg-radius-md)] px-3 py-2 text-xs font-mono inline-flex items-center gap-2 bg-black/30 backdrop-blur-sm">
          <LogOut size={14} /> <span className="hidden sm:inline">SIGN OUT</span>
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8">
        {/* SIDEBAR — all 11 modules visible at once */}
        <aside
          data-testid="admin-sidebar"
          className={`lg:col-span-3 ${mobileNavOpen ? 'block' : 'hidden lg:block'}`}
        >
          <div className="pg-admin-sidebar p-4 lg:sticky lg:top-24">
            {groupedTabs.map(({ group, items }) => (
              <div key={group} className="mb-4 last:mb-0">
                <p className="pg-label text-[10px] mb-2">{group.toUpperCase()}</p>
                <div className="flex flex-col gap-1">
                  {items.map((t) => {
                    const active = tab === t.id;
                    return (
                      <button
                        key={t.id}
                        data-testid={ADMIN.navTab}
                        onClick={() => { setTab(t.id); setMobileNavOpen(false); }}
                        className={`text-left px-3 py-2 rounded-[var(--pg-radius-sm)] text-xs font-mono tracking-[0.18em] uppercase border transition-colors ${
                          active
                            ? 'bg-[rgba(201,162,74,0.14)] border-[rgba(201,162,74,0.5)] text-[var(--pg-gold-2)]'
                            : 'border-transparent text-[var(--pg-ink-muted)] hover:text-[var(--pg-ink)] hover:bg-[rgba(242,238,227,0.04)]'
                        }`}
                      >
                        {t.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
            <div className="pg-hairline my-3" />
            <a href="/" className="block text-[10px] font-mono tracking-[0.32em] text-[var(--pg-ink-faint)] hover:text-[var(--pg-ink)] uppercase">← View public site</a>
          </div>
        </aside>

        {/* CONTENT */}
        <section className="lg:col-span-9 min-w-0">
          <div className="pg-admin-bar mb-4 flex items-baseline justify-between">
            <h2 className="font-display text-xl sm:text-2xl tracking-[0.04em] text-[var(--pg-ink)]">{currentTab.label.toUpperCase()}</h2>
            <span className="text-[10px] font-mono tracking-[0.32em] text-[var(--pg-ink-faint)] uppercase hidden sm:inline">{currentTab.group}</span>
          </div>

          {tab === 'home' && <HomeNotesAdmin />}
          {tab === 'meet_g' && <MeetGManager />}
          {tab === 'customers' && <CustomersManager />}
          {tab === 'integrations' && <IntegrationCenter />}
          {tab === 'orders' && <OrdersManager />}
          {tab === 'checkout' && <CheckoutSettingsManager />}
          {tab === 'discount_codes' && <DiscountCodesManager />}
          {tab === 'company_info' && <CompanyInfoManager />}
          {tab === 'legal' && <LegalDocumentsManager />}
          {tab === 'footer' && <FooterManager />}
          {tab === 'experience' && <ExperienceManager />}
          {tab === 'suggestions' && <SuggestionRulesManager />}
          {tab === 'behavioral' && <BehavioralInsights />}
          {tab === 'channels' && <ChannelsManager />}
          {tab === 'world' && <WorldEngineAdmin />}
          {/* Dynamic channel tabs (user-created channels not covered by a legacy tab). */}
          {tab.startsWith('channel__') && (
            <MixedChannelAdmin
              channelKey={currentTab.channelKey}
              channelLabel={currentTab.channelLabel}
            />
          )}
          {tab === 'music' && (
            <MixedChannelAdmin
              channelKey="music"
              channelLabel="Music"
            />
          )}
          {tab === 'podcasts' && (
            <CrudAdmin
              endpoint="audio"
              labelOne="episode"
              labelMany="episodes"
              defaults={defaults.audio_podcast}
              renderForm={AudioForm}
              renderRow={renderAudioRow}
              filter={(it) => it.kind === 'podcast'}
              channelKey="podcasts"
              channelLabel="Podcasts"
              nativeType="audio"
            />
          )}
          {tab === 'articles' && <CrudAdmin endpoint="articles" labelOne="article" labelMany="articles" defaults={defaults.articles} renderForm={ArticleForm} renderRow={renderArticleRow} channelKey="on_my_mind" channelLabel="On My Mind" nativeType="article" />}
          {tab === 'videos' && <CrudAdmin endpoint="videos" labelOne="video" labelMany="videos" defaults={defaults.videos} renderForm={VideoForm} renderRow={renderVideoRow} channelKey="watch" channelLabel="Watch" nativeType="video" />}
          {tab === 'products' && <CrudAdmin endpoint="products" labelOne="product" labelMany="products" defaults={defaults.products} renderForm={ProductForm} renderRow={renderProductRow} channelLabel="Vault" nativeType="product" />}
          {tab === 'marketing' && <MarketingManager />}
          {tab === 'collab' && <CollabSubmissionsAdmin />}
          {tab === 'subscribers' && <SubscribersAdmin />}
          {tab === 'intelligence' && <CommandIntelligence />}
          {tab === 'settings' && <SettingsManager />}
        </section>
      </div>
      </div>
    </div>
  );
}

function CrudAdmin({
  endpoint,
  labelOne,
  labelMany,
  defaults,
  renderForm,
  renderRow,
  filter,
  // Phase 13 — Universal Content Creation (optional)
  channelKey,       // e.g. 'music', 'videos', 'articles', 'products', 'mobile_reads'
  channelLabel,     // pretty label for the header ('Music', 'Watch', 'Vault' …)
  nativeType,       // one of ContentTypePicker keys — the native type for this channel
}) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(defaults);
  const [statusFilter, setStatusFilter] = useState('all');
  const [counts, setCounts] = useState({});
  // Universal Content picker state
  const [pickerOpen, setPickerOpen] = useState(false);
  const [activeConfig, setActiveConfig] = useState(null); // { endpoint, defaults, renderForm, labelOne, contentType }

  const load = async () => {
    setLoading(true);
    try {
      const params = statusFilter && statusFilter !== 'all' ? { status: statusFilter } : {};
      const { data } = await api.get(`/admin/${endpoint}`, { params });
      setItems(data);
      if (statusFilter === 'all') {
        const c = { all: data.length, published: 0, draft: 0, hidden: 0, archived: 0 };
        for (const it of data) {
          const s = it.publish_status || (it.published === false ? 'draft' : 'published');
          if (c[s] != null) c[s] += 1;
        }
        setCounts(c);
      }
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [endpoint, statusFilter]);

  const visible = filter ? items.filter(filter) : items;

  // Native (in-channel) create — uses the tab's own endpoint + form.
  const openCreateNative = () => {
    setActiveConfig(null);
    setEditing(null);
    const nativeDefaults = channelKey ? { ...defaults, channel_key: channelKey } : defaults;
    setForm(nativeDefaults);
    setOpen(true);
  };

  const openEdit = (item) => {
    setActiveConfig(null);
    setEditing(item);
    setForm({ ...defaults, ...item });
    setOpen(true);
  };

  // Universal content picker flow.
  const openPicker = () => setPickerOpen(true);
  const onPickType = (typeKey) => {
    setPickerOpen(false);
    // Native? → open the current form with channel_key pre-set.
    if (!nativeType || typeKey === nativeType) {
      openCreateNative();
      return;
    }
    // Non-native? → look up the config for that type and open a Drawer with its own form/endpoint.
    const cfg = CONTENT_TYPE_CONFIG[typeKey];
    if (!cfg) {
      alert('Unsupported content type');
      return;
    }
    setActiveConfig({ ...cfg, contentType: typeKey });
    setEditing(null);
    const baseDefaults = { ...cfg.defaults };
    if (channelKey) baseDefaults.channel_key = channelKey;
    if (typeKey === 'external_link' || typeKey === 'standard') baseDefaults.content_type = typeKey;
    if (typeKey === 'digital_download') {
      baseDefaults.fulfillment_provider = 'digital';
      baseDefaults.content_type = 'digital_download';
    }
    setForm(baseDefaults);
    setOpen(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const cleanForm = Object.fromEntries(Object.entries(form).filter(([k]) => !k.startsWith('_')));
      const targetEndpoint = activeConfig ? activeConfig.endpoint : endpoint;
      if (editing) {
        await api.put(`/admin/${targetEndpoint}/${editing.id}`, cleanForm);
      } else {
        await api.post(`/admin/${targetEndpoint}`, cleanForm);
      }
      setOpen(false);
      setActiveConfig(null);
      await load();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!window.confirm('Delete this item?')) return;
    try {
      await api.delete(`/admin/${endpoint}/${id}`);
      await load();
    } catch (e) { alert(e?.response?.data?.detail || 'Delete failed'); }
  };

  // Determine active form / labels depending on active config (universal creator vs native)
  const activeRenderForm = activeConfig ? activeConfig.renderForm : renderForm;
  const activeLabelOne = activeConfig ? activeConfig.labelOne : labelOne;
  const drawerTitle = editing
    ? `Edit ${labelOne}`
    : (activeConfig ? `New ${activeLabelOne}` : `New ${labelOne}`);

  return (
    <div>
      <div className="pg-admin-bar flex items-center justify-between mb-4">
        <p className="text-sm text-[var(--pg-ink)] font-mono">{loading ? 'Loading…' : `${visible.length} ${labelMany}`}</p>
        {channelKey ? (
          <button
            data-testid={ADMIN.createButton}
            onClick={openPicker}
            className="pg-btn-primary rounded-[var(--pg-radius-md)] px-4 py-2 text-xs font-mono tracking-widest inline-flex items-center gap-2"
          >
            <Plus size={14} /> ADD CONTENT
          </button>
        ) : (
          <button
            data-testid={ADMIN.createButton}
            onClick={openCreateNative}
            className="pg-btn-primary rounded-[var(--pg-radius-md)] px-4 py-2 text-xs font-mono tracking-widest inline-flex items-center gap-2"
          >
            <Plus size={14} /> NEW {labelOne.toUpperCase()}
          </button>
        )}
      </div>

      <PublishStatusTabs value={statusFilter} onChange={setStatusFilter} counts={counts} />

      <div className="pg-card overflow-hidden">
        {visible.length === 0 ? (
          <div className="p-10 text-center text-[var(--pg-ink-muted)]">No {labelMany} yet. Create the first.</div>
        ) : (
          <ul className="divide-y divide-[var(--pg-border-2)]">
            {visible.map((it) => (
              <li key={it.id} className="p-4 flex items-center gap-4">
                {renderRow(it)}
                <PublishStatusBadge status={it.publish_status || (it.published === false ? 'draft' : 'published')} />
                <div className="flex items-center gap-2">
                  <button onClick={() => openEdit(it)} className="pg-btn-ghost rounded-[var(--pg-radius-sm)] px-3 py-1.5 text-xs">Edit</button>
                  <button data-testid={ADMIN.deleteButton} onClick={() => del(it.id)} className="pg-btn-ghost rounded-[var(--pg-radius-sm)] px-3 py-1.5 text-xs text-[var(--pg-neon-pink)]"><Trash2 size={14} /></button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Universal Content Type Picker (only when channelKey provided) */}
      <ContentTypePicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onSelect={onPickType}
        channelKey={channelKey}
        channelLabel={channelLabel}
        nativeType={nativeType}
      />

      <Drawer open={open} onClose={() => { setOpen(false); setActiveConfig(null); }} title={drawerTitle} onSave={save} saving={saving}>
        {activeRenderForm(form, setForm)}
      </Drawer>
    </div>
  );
}

function renderAudioRow(it) {
  return (
    <div className="flex-1 min-w-0">
      <p className="font-serif text-base text-[var(--pg-ink)] truncate">{it.title}</p>
      <p className="text-xs text-[var(--pg-ink-muted)] mt-0.5">{it.kind} · {it.source_type}{it.featured ? ' · featured' : ''}{!it.published ? ' · hidden' : ''}</p>
    </div>
  );
}

function AudioForm(form, setForm) {
  const set = (k) => (v) => setForm({ ...form, [k]: v });
  return (
    <>
      <TextField label="Title" value={form.title} onChange={set('title')} />
      <TextField label="Description" value={form.description} onChange={set('description')} textarea />
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs font-mono tracking-widest text-[var(--pg-ink-muted)]">Kind</label>
          <select value={form.kind} onChange={(e) => set('kind')(e.target.value)} className="mt-1 w-full pg-input rounded-[var(--pg-radius-sm)] px-3 py-2 text-sm">
            <option value="music">Music</option><option value="podcast">Podcast</option><option value="reflection">Reflection</option>
          </select>
        </div>
        <div>
          <label className="text-xs font-mono tracking-widest text-[var(--pg-ink-muted)]">Source</label>
          <select value={form.source_type} onChange={(e) => set('source_type')(e.target.value)} className="mt-1 w-full pg-input rounded-[var(--pg-radius-sm)] px-3 py-2 text-sm">
            <option value="upload">Upload</option><option value="embed">Embed</option>
          </select>
        </div>
      </div>
      {form.source_type === 'upload' && <FileField label="Audio File" kind="audio" value={form.file_url} onChange={set('file_url')} accept=".mp3,.m4a,.wav,.aac,.ogg,.flac,.mp4,.mpga,audio/mpeg,audio/mp3,audio/mp4,audio/x-m4a,audio/m4a,audio/wav,audio/x-wav,audio/aac,audio/ogg,audio/flac,audio/*" />}
      {form.source_type === 'embed' && <TextField label="Embed URL (Spotify/SoundCloud/YouTube)" value={form.embed_url} onChange={set('embed_url')} />}
      <FileField label="Cover Image" kind="image" value={form.cover_url} onChange={set('cover_url')} accept="image/*" />
      <TextField label="Duration" value={form.duration} onChange={set('duration')} placeholder="e.g. 3:42" />
      <TagsField label="Tags" value={form.tags} onChange={set('tags')} />
      <PublishStatusControl value={form.publish_status || 'published'} onChange={(v) => setForm({ ...form, publish_status: v, published: v === 'published' })} />
      <Toggle label="Featured" value={form.featured} onChange={set('featured')} />
      <DiscoveryPanel contentType="audio_item" contentId={form.id} defaultTitle={form.title} defaultImage={form.cover_url} />
    </>
  );
}

function renderArticleRow(it) {
  return (
    <div className="flex-1 min-w-0">
      <p className="font-serif text-base text-[var(--pg-ink)] truncate">{it.title}</p>
      <p className="text-xs text-[var(--pg-ink-muted)] mt-0.5 font-mono">/{it.slug}{it.featured ? ' · featured' : ''}{!it.published ? ' · hidden' : ''}</p>
    </div>
  );
}

function slugify(text) {
  return String(text || '')
    .toLowerCase()
    .normalize('NFKD').replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9\-\s]+/g, '')
    .trim()
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-');
}

function ArticleForm(form, setForm) {
  const set = (k) => (v) => setForm({ ...form, [k]: v });
  // Editing an existing article? Slug is already populated → treat as touched.
  // Brand-new article with empty slug? Auto-fill as the user types the title.
  const slugIsTouched = form._slug_touched === true || (form._slug_touched === undefined && !!(form.slug && form.slug.trim()));
  const onTitleChange = (v) => {
    setForm({
      ...form,
      title: v,
      slug: slugIsTouched ? form.slug : slugify(v),
    });
  };
  const onSlugChange = (v) => {
    setForm({
      ...form,
      slug: v.toLowerCase().replace(/[^a-z0-9\-]+/g, '-').replace(/-+/g, '-'),
      _slug_touched: true,
    });
  };
  return (
    <>
      <TextField label="Title" value={form.title} onChange={onTitleChange} />
      <TextField label="Subtitle" value={form.subtitle} onChange={set('subtitle')} />
      <TextField
        label="Slug (auto-generated from title — edit to customise)"
        value={form.slug}
        onChange={onSlugChange}
        placeholder="slow-attention"
      />
      <TextField label="Excerpt" value={form.excerpt} onChange={set('excerpt')} textarea rows={2} />
      <div>
        <label className="text-xs font-mono tracking-widest text-[var(--pg-ink-muted)]">Body</label>
        <p className="text-[10px] text-[var(--pg-ink-faint)] mt-0.5 mb-2">Use the toolbar to format. Insert internal links to other channels/articles/products/mobile reads.</p>
        <RichTextEditor value={form.body} onChange={set('body')} placeholder="Start writing…" minHeight={340} testId="article-body-editor" />
      </div>
      <FileField label="Cover Image" kind="image" value={form.cover_url} onChange={set('cover_url')} accept="image/*" />
      <TextField label="Read time" value={form.read_time} onChange={set('read_time')} placeholder="6 min read" />
      <TagsField label="Tags" value={form.tags} onChange={set('tags')} />
      <PublishStatusControl value={form.publish_status || 'published'} onChange={(v) => setForm({ ...form, publish_status: v, published: v === 'published' })} />
      <Toggle label="Featured" value={form.featured} onChange={set('featured')} />
      <DiscoveryPanel contentType="article" contentId={form.id} defaultTitle={form.title} defaultImage={form.cover_url} />
    </>
  );
}

function renderGalleryRow(it) {
  return (
    <div className="flex-1 min-w-0 flex items-center gap-3">
      {it.image_url && <img src={mediaUrl(it.image_url)} alt="" className="h-12 w-16 object-cover rounded-md border border-[var(--pg-border-2)]" />}
      <div className="min-w-0">
        <p className="font-serif text-base text-[var(--pg-ink)] truncate">{it.title}</p>
        <p className="text-xs text-[var(--pg-ink-muted)] truncate">{it.caption || ''}</p>
      </div>
    </div>
  );
}

function GalleryForm(form, setForm) {
  const set = (k) => (v) => setForm({ ...form, [k]: v });
  return (
    <>
      <TextField label="Title" value={form.title} onChange={set('title')} />
      <TextField label="Caption" value={form.caption} onChange={set('caption')} textarea rows={2} />
      <FileField label="Image" kind="image" value={form.image_url} onChange={set('image_url')} accept="image/*" />
      <TagsField label="Tags" value={form.tags} onChange={set('tags')} />
      <PublishStatusControl value={form.publish_status || 'published'} onChange={(v) => setForm({ ...form, publish_status: v, published: v === 'published' })} />
      <DiscoveryPanel contentType="gallery_item" contentId={form.id} defaultTitle={form.title} defaultImage={form.image_url} />
    </>
  );
}

// External Link content-type form (stored in the articles collection with
// content_type = "external_link"). Minimal fields: title, external URL, cover.
function ExternalLinkForm(form, setForm) {
  const set = (k) => (v) => setForm({ ...form, [k]: v });
  const onTitleChange = (v) => {
    setForm({
      ...form,
      title: v,
      // Auto-generate a slug for uniqueness even though external links don't render an article page.
      slug: form._slug_touched ? form.slug : (v ? slugify(v) : ''),
    });
  };
  return (
    <>
      <p className="pg-label text-[10px]">EXTERNAL LINK</p>
      <p className="text-[11px] text-[var(--pg-ink-muted)] -mt-1">A link tile that redirects visitors to an external URL. Behavior: redirect. Presentation: standard link card.</p>
      <TextField label="Title" value={form.title} onChange={onTitleChange} />
      <TextField
        label="External URL (required)"
        value={form.external_url || ''}
        onChange={set('external_url')}
        placeholder="https://…"
      />
      <TextField label="Subtitle (optional)" value={form.subtitle} onChange={set('subtitle')} />
      <TextField label="Excerpt (optional)" value={form.excerpt} onChange={set('excerpt')} textarea rows={2} />
      <FileField label="Cover Image (optional)" kind="image" value={form.cover_url} onChange={set('cover_url')} accept="image/*" />
      <TagsField label="Tags" value={form.tags} onChange={set('tags')} />
      <PublishStatusControl value={form.publish_status || 'published'} onChange={(v) => setForm({ ...form, publish_status: v, published: v === 'published' })} />
      <DiscoveryPanel contentType="external_link" contentId={form.id} defaultTitle={form.title} defaultImage={form.cover_url} />
    </>
  );
}

function renderVideoRow(it) {
  return (
    <div className="flex-1 min-w-0">
      <p className="font-serif text-base text-[var(--pg-ink)] truncate">{it.title}</p>
      <p className="text-xs text-[var(--pg-ink-muted)] truncate font-mono">{it.embed_url}</p>
    </div>
  );
}

function VideoForm(form, setForm) {
  const set = (k) => (v) => setForm({ ...form, [k]: v });
  return (
    <>
      <TextField label="Title" value={form.title} onChange={set('title')} />
      <TextField label="Description" value={form.description} onChange={set('description')} textarea />
      <TextField label="Embed URL (YouTube/Vimeo)" value={form.embed_url} onChange={set('embed_url')} placeholder="https://www.youtube.com/watch?v=..." />
      <FileField label="Poster Image" kind="image" value={form.poster_url} onChange={set('poster_url')} accept="image/*" />
      <TagsField label="Tags" value={form.tags} onChange={set('tags')} />
      <PublishStatusControl value={form.publish_status || 'published'} onChange={(v) => setForm({ ...form, publish_status: v, published: v === 'published' })} />
      <Toggle label="Featured" value={form.featured} onChange={set('featured')} />
      <DiscoveryPanel contentType="video" contentId={form.id} defaultTitle={form.title} defaultImage={form.poster_url} />
    </>
  );
}

function renderProductRow(it) {
  return (
    <div className="flex-1 min-w-0 flex items-center gap-3">
      {it.cover_url && <img src={mediaUrl(it.cover_url)} alt="" className="h-12 w-12 object-cover rounded-md border border-[var(--pg-border-2)]" />}
      <div className="min-w-0">
        <p className="font-serif text-base text-[var(--pg-ink)] truncate">{it.title}</p>
        <p className="text-xs text-[var(--pg-gold-2)] font-mono">${it.price?.toFixed?.(2) ?? it.price} {(it.currency || 'usd').toUpperCase()}{!it.published ? ' · hidden' : ''}</p>
      </div>
    </div>
  );
}

/**
 * ChannelSelector — dropdown that lists all channels (from /api/admin/channels),
 * writes the selected channel's `key` to the parent form's `channel_key` field, and
 * includes an explicit "None (unassigned)" option.
 *
 * Used by ProductForm so admins can route ANY product (Printful-imported OR manually
 * created) to any channel — e.g. assigning an imported print to "Art Gallery".
 */
function ChannelSelector({ value, onChange, testId = 'product-channel-selector' }) {
  const [channels, setChannels] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  React.useEffect(() => {
    let cancelled = false;
    api.get('/admin/channels')
      .then(({ data }) => { if (!cancelled) setChannels(Array.isArray(data) ? data : []); })
      .catch(() => { if (!cancelled) setChannels([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);
  const selectedLabel = React.useMemo(() => {
    if (!value) return 'None (unassigned)';
    const c = channels.find((x) => x.key === value);
    return c ? `${c.label} (${c.key})` : `${value} · not found`;
  }, [value, channels]);
  return (
    <div className="pg-card p-3 -mx-1" data-testid={`${testId}-card`}>
      <p className="pg-label text-[10px] mb-1">CHANNEL ASSIGNMENT</p>
      <p className="text-[11px] text-[var(--pg-ink-muted)] mb-2">
        Choose which channel this product appears in (e.g. Art Gallery, Vault). Applies to every
        product — whether imported from Printful or created manually. Leave as None to keep it unassigned.
      </p>
      <label className="text-xs font-mono tracking-widest text-[var(--pg-ink-muted)]">Channel</label>
      <select
        value={value || ''}
        onChange={(e) => onChange(e.target.value || null)}
        disabled={loading}
        data-testid={testId}
        className="mt-1 w-full pg-input rounded-[var(--pg-radius-sm)] px-3 py-2 text-sm disabled:opacity-60"
      >
        <option value="">None (unassigned)</option>
        {channels.map((c) => (
          <option key={c.key} value={c.key}>{c.label} — /{c.route || c.key}</option>
        ))}
      </select>
      <p className="text-[10px] font-mono text-[var(--pg-ink-faint)] mt-1">
        {loading ? 'Loading channels…' : `Current: ${selectedLabel}`}
      </p>
    </div>
  );
}

function ProductForm(form, setForm) {
  const set = (k) => (v) => setForm({ ...form, [k]: v });

  // Fulfillment Type - user-facing labels mapped to existing stored fields.
  // Digital Download → fulfillment_provider = "digital"
  // Printful Fulfillment → fulfillment_provider = "printful"
  // Subscription → is_subscription = true (uses Stripe recurring)
  // Physical Product → fulfillment_provider = "manual"
  const fulfillmentType = form.is_subscription
    ? 'subscription'
    : (form.fulfillment_provider === 'digital' ? 'digital_download'
    : form.fulfillment_provider === 'printful' ? 'printful'
    : form.fulfillment_provider === 'manual' ? 'physical'
    : 'physical');

  const changeFulfillmentType = (val) => {
    const next = { ...form };
    switch (val) {
      case 'digital_download':
        next.fulfillment_provider = 'digital';
        next.is_subscription = false;
        break;
      case 'printful':
        next.fulfillment_provider = 'printful';
        next.is_subscription = false;
        break;
      case 'subscription':
        // Keep provider for delivery; subscription is orthogonal
        next.is_subscription = true;
        if (!next.subscription_interval) next.subscription_interval = 'month';
        break;
      case 'physical':
      default:
        next.fulfillment_provider = 'manual';
        next.is_subscription = false;
        break;
    }
    next.content_type = val === 'digital_download' ? 'digital_download' : 'product';
    setForm(next);
  };

  return (
    <>
      {/* --- Product Metadata --- */}
      <div className="pg-card p-3 -mx-1">
        <p className="pg-label text-[10px] mb-2">PRODUCT METADATA</p>
        <div className="space-y-3">
          <TextField label="Title" value={form.title} onChange={set('title')} />
          <TextField label="Subtitle" value={form.subtitle} onChange={set('subtitle')} />
          <div>
            <label className="text-xs font-mono tracking-widest text-[var(--pg-ink-muted)]">Description</label>
            <div className="mt-2">
              <RichTextEditor value={form.description} onChange={set('description')} placeholder="Describe this product…" minHeight={200} testId="vault-product-description-editor" />
            </div>
          </div>
          <FileField label="Cover Image" kind="image" value={form.cover_url} onChange={set('cover_url')} accept="image/*" />
          <TagsField label="Tags" value={form.tags} onChange={set('tags')} />
        </div>
      </div>

      {/* --- Merchandising Gallery (editorial + Printful merged) --- */}
      <ProductGalleryEditor
        printfulImages={form.gallery_images || []}
        editorialImages={form.user_gallery_images || []}
        order={form.gallery_order || []}
        primary={form.primary_image_url || ''}
        onChange={(patch) => setForm({ ...form, ...patch })}
      />

      {/* --- Fulfillment Type (user-facing) --- */}
      <div className="pg-card p-3 -mx-1">
        <p className="pg-label text-[10px] mb-2">FULFILLMENT TYPE</p>
        <p className="text-[11px] text-[var(--pg-ink-muted)] mb-2">Determines how this product is delivered after checkout.</p>
        <div>
          <label className="text-xs font-mono tracking-widest text-[var(--pg-ink-muted)]">Type</label>
          <select
            value={fulfillmentType}
            onChange={(e) => changeFulfillmentType(e.target.value)}
            data-testid="product-fulfillment-type"
            className="mt-1 w-full pg-input rounded-[var(--pg-radius-sm)] px-3 py-2 text-sm"
          >
            <option value="digital_download">Digital Download</option>
            <option value="printful">Printful Fulfillment</option>
            <option value="subscription">Subscription</option>
            <option value="physical">Physical Product</option>
          </select>
          <p className="text-[10px] font-mono text-[var(--pg-ink-faint)] mt-1">
            {fulfillmentType === 'digital_download' && 'Delivered as a downloadable file via signed link after payment.'}
            {fulfillmentType === 'printful' && 'Order routed automatically to Printful for print-on-demand fulfillment.'}
            {fulfillmentType === 'subscription' && 'Recurring billing via Stripe. Digital access granted while active.'}
            {fulfillmentType === 'physical' && 'Standard shipping workflow — you (or your fulfillment partner) ship manually.'}
          </p>
        </div>

        {fulfillmentType === 'digital_download' && (
          <div className="mt-3 space-y-2">
            <FileField
              label="Download File (delivered after purchase)"
              kind="file"
              value={form.download_url}
              onChange={(url, meta) => setForm((f) => ({
                ...f,
                download_url: url,
                // Only overwrite download_filename on a fresh upload (meta present).
                // Manual URL edits leave the existing filename untouched.
                ...(meta && meta.original_filename
                  ? { download_filename: meta.original_filename }
                  : {}),
              }))}
              accept=".pdf,.zip,.epub,.mp3,.wav"
            />
            {form.download_filename && (
              <p className="text-[10px] font-mono text-[var(--pg-ink-faint)] break-all" data-testid="product-download-filename">
                Filename customers will receive: <span className="text-[var(--pg-ink-muted)]">{form.download_filename}</span>
              </p>
            )}
            <TextField label="Format label" value={form.file_format} onChange={set('file_format')} placeholder="PDF / MP3 / ZIP" />
          </div>
        )}

        {fulfillmentType === 'printful' && (
          <div className="mt-3 space-y-3" data-testid="product-printful-section">
            <PrintfulCatalogPicker
              productId={form.printful_sync_product_id}
              variantId={form.printful_sync_variant_id}
              onChange={(patch) => setForm({ ...form, ...patch })}
              onSelectVariant={(v) => {
                // If the admin hasn't set a price yet, pre-fill from the variant's retail price.
                if (v?.retail_price && (!form.price || Number(form.price) <= 0)) {
                  setForm((f) => ({ ...f, ...(v?.retail_price ? { price: Number(v.retail_price) } : {}) }));
                }
              }}
            />
            {/* Manual fallback — always visible below the picker so admins can
                paste IDs if the picker is unavailable or the desired variant
                isn't listed yet (e.g., just published in Printful, not synced). */}
            <details className="pg-card p-3">
              <summary className="cursor-pointer text-[10px] font-mono tracking-widest text-[var(--pg-ink-muted)]" data-testid="pf-picker-manual-fallback-toggle">
                MANUAL FALLBACK · PASTE IDS
              </summary>
              <div className="mt-3 space-y-2">
                <TextField
                  label="Printful sync product ID"
                  value={form.printful_sync_product_id}
                  onChange={set('printful_sync_product_id')}
                  placeholder="e.g. 340123456"
                />
                <TextField
                  label="Printful sync variant ID"
                  value={form.printful_sync_variant_id}
                  onChange={set('printful_sync_variant_id')}
                  placeholder="e.g. 4712345678"
                />
                <p className="text-[10px] font-mono text-[var(--pg-ink-faint)]">
                  Use this fallback only when the catalog picker can&apos;t reach Printful or a brand-new variant hasn&apos;t synced yet.
                </p>
              </div>
            </details>
          </div>
        )}

        {fulfillmentType === 'subscription' && (
          <div className="mt-3">
            <label className="text-xs font-mono tracking-widest text-[var(--pg-ink-muted)]">Billing interval</label>
            <select value={form.subscription_interval || 'month'} onChange={(e) => set('subscription_interval')(e.target.value)} className="mt-1 w-full pg-input rounded-[var(--pg-radius-sm)] px-3 py-2 text-sm">
              <option value="month">Monthly</option>
              <option value="year">Yearly</option>
            </select>
          </div>
        )}

        {fulfillmentType === 'physical' && (
          <div className="mt-3 grid grid-cols-2 gap-3">
            <Toggle label="Track inventory" value={form.inventory_tracking} onChange={set('inventory_tracking')} />
            {form.inventory_tracking && (
              <TextField label="Quantity on hand" type="number" value={form.inventory_quantity ?? 0} onChange={set('inventory_quantity')} />
            )}
          </div>
        )}
      </div>

      {/* --- Pricing --- */}
      <div className="pg-card p-3 -mx-1">
        <p className="pg-label text-[10px] mb-2">PRICING</p>
        <div className="grid grid-cols-2 gap-3">
          <TextField label="Price (min 0.50)" value={form.price} onChange={set('price')} type="number" />
          <div>
            <label className="text-xs font-mono tracking-widest text-[var(--pg-ink-muted)]">Currency</label>
            <select value={form.currency} onChange={(e) => set('currency')(e.target.value)} className="mt-1 w-full pg-input rounded-[var(--pg-radius-sm)] px-3 py-2 text-sm">
              <option value="usd">USD</option><option value="eur">EUR</option><option value="gbp">GBP</option>
            </select>
          </div>
        </div>
        {/* Free download toggle — visible ONLY for digital downloads. When
            enabled, the checkout flow skips Stripe entirely and the customer
            is taken straight to the download link. */}
        {fulfillmentType === 'digital_download' && (
          <div className="mt-3" data-testid="product-is-free-wrap">
            <Toggle
              label="Free download"
              value={!!form.is_free}
              onChange={(v) => setForm((f) => ({ ...f, is_free: !!v }))}
            />
            {form.is_free && (
              <p className="mt-1 text-[10px] font-mono text-[var(--pg-ink-faint)]">
                Checkout will bypass payment and deliver the download instantly.
              </p>
            )}
          </div>
        )}
      </div>

      {/* --- Publishing --- */}
      <PublishStatusControl value={form.publish_status || 'published'} onChange={(v) => setForm({ ...form, publish_status: v, published: v === 'published' })} />
      <Toggle label="Featured" value={form.featured} onChange={set('featured')} />

      {/* --- Channel Assignment (applies to ALL products) --- */}
      <ChannelSelector
        value={form.channel_key}
        onChange={(v) => setForm({ ...form, channel_key: v })}
      />

      {/* --- Discovery + Related Content --- */}
      <DiscoveryPanel contentType="product" contentId={form.id} defaultTitle={form.title} defaultImage={form.cover_url} />

      {/* --- Presentation / Template Assignment --- */}
      <TemplateAssignmentControl targetType="product" targetId={form.id} />
    </>
  );
}

function renderProjectRow(it) {
  return (
    <div className="flex-1 min-w-0">
      <p className="font-serif text-base text-[var(--pg-ink)] truncate">{it.title}</p>
      <p className="text-xs text-[var(--pg-ink-muted)] mt-0.5 font-mono">{it.status} · {it.progress ?? 0}%</p>
    </div>
  );
}

function ProjectForm(form, setForm) {
  const set = (k) => (v) => setForm({ ...form, [k]: v });
  return (
    <>
      <TextField label="Title" value={form.title} onChange={set('title')} />
      <TextField label="Description" value={form.description} onChange={set('description')} textarea rows={5} />
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs font-mono tracking-widest text-[var(--pg-ink-muted)]">Status</label>
          <select value={form.status} onChange={(e) => set('status')(e.target.value)} className="mt-1 w-full pg-input rounded-[var(--pg-radius-sm)] px-3 py-2 text-sm">
            <option value="seeded">Seeded</option><option value="in_progress">In progress</option><option value="coming_soon">Coming soon</option>
          </select>
        </div>
        <TextField label="Progress (0–100)" value={form.progress} onChange={set('progress')} type="number" />
      </div>
      <FileField label="Cover Image" kind="image" value={form.cover_url} onChange={set('cover_url')} accept="image/*" />
      <TagsField label="Tags" value={form.tags} onChange={set('tags')} />
      <PublishStatusControl value={form.publish_status || 'published'} onChange={(v) => setForm({ ...form, publish_status: v, published: v === 'published' })} />
    </>
  );
}

function HomeNotesAdmin() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(defaults.home);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get('/admin/home/notes'); setItems(data); } catch (e) {} finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.body.trim()) return alert('Body is required');
    setSaving(true);
    try {
      await api.post('/admin/home/notes', form);
      setForm(defaults.home);
      await load();
    } catch (e) { alert(e?.response?.data?.detail || 'Save failed'); } finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!window.confirm('Delete this note?')) return;
    try { await api.delete(`/admin/home/notes/${id}`); await load(); } catch (e) { alert(e?.response?.data?.detail || 'Delete failed'); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div className="pg-card p-5">
        <p className="pg-label">Add a homepage note</p>
        <h3 className="font-serif text-xl mt-2 mb-4">Speak to the room.</h3>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-mono tracking-widest text-[var(--pg-ink-muted)]">Kind</label>
            <select value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })} className="mt-1 w-full pg-input rounded-[var(--pg-radius-sm)] px-3 py-2 text-sm">
              <option value="daily_drop">Daily Drop</option>
              <option value="on_my_mind">What's On My Mind</option>
            </select>
          </div>
          <TextField label="Title (optional)" value={form.title} onChange={(v) => setForm({ ...form, title: v })} />
          <div>
            <label className="text-xs font-mono tracking-widest text-[var(--pg-ink-muted)]">Body</label>
            <div className="mt-2">
              <RichTextEditor value={form.body} onChange={(v) => setForm({ ...form, body: v })} placeholder="Share a thought…" minHeight={160} testId="home-note-body-editor" />
            </div>
          </div>
          <button data-testid={ADMIN.createButton} onClick={create} disabled={saving} className="pg-btn-primary rounded-[var(--pg-radius-md)] px-4 py-2 text-xs font-mono tracking-widest disabled:opacity-60 inline-flex items-center gap-2">
            <Plus size={14} /> {saving ? 'Saving…' : 'POST NOTE'}
          </button>
        </div>
      </div>

      <div className="pg-card p-5">
        <p className="pg-label">Recent notes</p>
        <h3 className="font-serif text-xl mt-2 mb-4">From the lounge.</h3>
        {loading ? <p className="text-sm text-[var(--pg-ink-muted)]">Loading…</p> : items.length === 0 ? <p className="text-sm text-[var(--pg-ink-muted)]">No notes yet.</p> : (
          <ul className="space-y-3 max-h-[60vh] overflow-y-auto pg-scroll pr-2">
            {items.map(n => (
              <li key={n.id} className="pg-card p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="pg-label text-[10px]">{n.kind === 'daily_drop' ? 'Daily Drop' : "What's on my mind"}</p>
                    {n.title && <p className="font-serif text-base text-[var(--pg-ink)] mt-1">{n.title}</p>}
                    <p className="text-sm text-[var(--pg-ink-muted)] mt-1 whitespace-pre-wrap">{n.body}</p>
                    <p className="text-[10px] font-mono text-[var(--pg-ink-faint)] mt-2">{new Date(n.created_at).toLocaleString()}</p>
                  </div>
                  <button onClick={() => del(n.id)} className="text-[var(--pg-neon-pink)] p-1 hover:opacity-80"><Trash2 size={14} /></button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function SubscribersAdmin() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get('/admin/subscribers'); setItems(data); } catch (e) {} finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const downloadCsv = () => {
    const csv = ['email,created_at,mailchimp_status', ...items.map(s => `${s.email},${s.created_at},${s.mailchimp_status || ''}`)].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'planet-g-subscribers.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const syncMailchimp = async () => {
    try {
      const { data } = await api.post('/admin/subscribers/sync-mailchimp');
      alert(`Synced ${data.updated}. Failed ${data.failed}.`);
      await load();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Sync failed — Mailchimp keys missing?');
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <p className="text-sm text-[var(--pg-ink-muted)] font-mono">{loading ? 'Loading…' : `${items.length} letters subscribed`}</p>
        <div className="flex gap-2">
          <button onClick={syncMailchimp} className="pg-btn-ghost rounded-[var(--pg-radius-sm)] px-3 py-2 text-xs font-mono">Sync Mailchimp</button>
          <button onClick={downloadCsv} className="pg-btn-primary rounded-[var(--pg-radius-sm)] px-3 py-2 text-xs font-mono tracking-widest">EXPORT CSV</button>
        </div>
      </div>

      <div className="pg-card overflow-hidden">
        {items.length === 0 ? (
          <div className="p-10 text-center text-[var(--pg-ink-muted)]">No subscribers yet.</div>
        ) : (
          <ul className="divide-y divide-[var(--pg-border-2)]">
            {items.map(s => (
              <li key={s.email_lower} className="px-4 py-3 flex items-center justify-between gap-3">
                <span className="text-[var(--pg-ink)] text-sm truncate">{s.email}</span>
                <span className="font-mono text-[11px] text-[var(--pg-ink-faint)]">{s.mailchimp_status || 'pending'} · {new Date(s.created_at).toLocaleDateString()}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}


function WorldEngineAdmin() {
  const [state, setState] = useState(null);
  const [envs, setEnvs] = useState([]);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [newEnv, setNewEnv] = useState({ name: '', image_url: '', description: '', palette: { primary: '#E5BFC8', secondary: '#C9B0CA', tertiary: '#9FAE92', accent: '#EBC9A2', deep: '#917A8B', ink: '#FBF6EE', ink_contrast: '#2A1F26' } });

  const loadAll = () => {
    Promise.all([
      api.get('/planetsync').then(({ data }) => setState(data)),
      api.get('/admin/environments').then(({ data }) => setEnvs(data)),
    ]).catch(() => {});
  };
  useEffect(() => { loadAll(); }, []);

  if (!state) return <div className="pg-card p-10 text-center text-[var(--pg-ink-muted)]">Loading PlanetSync…</div>;

  const update = (k, v) => setState((s) => ({ ...s, [k]: v }));

  const save = async () => {
    setSaving(true); setMsg(null);
    try { const { data } = await api.put('/admin/planetsync', state); setState(data); setMsg('Published. The world shifts.'); }
    catch (e) { setMsg(e?.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  const uploadEnvImage = async (file) => {
    const fd = new FormData(); fd.append('file', file);
    const { data } = await api.post('/admin/uploads/image', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
    return data.url;
  };

  const onAddEnv = async (e) => {
    e.preventDefault();
    if (!newEnv.name || !newEnv.image_url) return alert('Name and image required');
    try { await api.post('/admin/environments', { ...newEnv, order: envs.length + 1 }); setNewEnv({ ...newEnv, name: '', image_url: '', description: '' }); loadAll(); }
    catch (err) { alert(err?.response?.data?.detail || 'Save failed'); }
  };

  const selectEnv = async (env) => {
    setState((s) => ({ ...s, background_environment_id: env.id, background_image_url: env.image_url, palette: env.palette }));
    try { const { data } = await api.put('/admin/planetsync', { background_environment_id: env.id, background_mode: 'manual' }); setState(data); setMsg(`Active: ${env.name}`); }
    catch (e) { alert(e?.response?.data?.detail || 'Switch failed'); }
  };

  const deleteEnv = async (id) => {
    if (!window.confirm('Delete this environment?')) return;
    await api.delete(`/admin/environments/${id}`); loadAll();
  };

  const patchEnv = async (id, patch) => {
    try { await api.put(`/admin/environments/${id}`, patch); loadAll(); }
    catch (e) { alert(e?.response?.data?.detail || 'Update failed'); }
  };

  const moveEnv = async (idx, dir) => {
    const target = idx + dir;
    if (target < 0 || target >= envs.length) return;
    const next = envs.slice();
    [next[idx], next[target]] = [next[target], next[idx]];
    setEnvs(next);
    try { await api.post('/admin/environments/reorder', { order: next.map((e, i) => ({ id: e.id, order: i + 1 })) }); loadAll(); }
    catch (e) { alert(e?.response?.data?.detail || 'Reorder failed'); }
  };

  const Sel = ({ label, k, opts }) => (
    <div>
      <label className="text-xs font-mono tracking-widest text-[var(--pg-ink-muted)]">{label}</label>
      <select value={state[k]} onChange={(e) => update(k, e.target.value)} className="mt-1 w-full pg-input rounded-[var(--pg-radius-sm)] px-3 py-2 text-sm">
        {opts.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );

  const Num = ({ label, k, min, max, step }) => (
    <div>
      <label className="text-xs font-mono tracking-widest text-[var(--pg-ink-muted)]">{label} ({(state[k] || 0).toFixed?.(2)})</label>
      <input type="range" min={min} max={max} step={step} value={state[k] || 0} onChange={(e) => update(k, parseFloat(e.target.value))} className="mt-2 w-full accent-[var(--pg-primary)]" />
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Background controller */}
      <div className="pg-card p-6">
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <p className="pg-label">BACKGROUND CONTROLLER</p>
            <h3 className="font-display text-2xl mt-1 tracking-[0.04em]">WORLD ENGINE ENVIRONMENTS</h3>
            <p className="text-sm text-[var(--pg-ink-muted)] mt-2 max-w-2xl">Upload backdrops. Pick a mode. Publishing applies the image and palette immediately across every page.</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => update('background_mode', 'manual')} className={`px-3 py-2 rounded-full text-xs font-mono tracking-widest border ${state.background_mode === 'manual' ? 'border-[color-mix(in_srgb,var(--pg-primary)_55%,transparent)] text-[var(--pg-primary)] bg-[color-mix(in_srgb,var(--pg-primary)_10%,transparent)]' : 'border-[var(--pg-border-2)] text-[var(--pg-ink-muted)]'}`}>MANUAL</button>
            <button onClick={() => update('background_mode', 'auto_rotate')} className={`px-3 py-2 rounded-full text-xs font-mono tracking-widest border ${state.background_mode === 'auto_rotate' ? 'border-[color-mix(in_srgb,var(--pg-tertiary)_55%,transparent)] text-[var(--pg-tertiary)] bg-[color-mix(in_srgb,var(--pg-tertiary)_10%,transparent)]' : 'border-[var(--pg-border-2)] text-[var(--pg-ink-muted)]'}`}>AUTO ROTATE</button>
          </div>
        </div>

        {/* Environment grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
          {envs.map((e, ei) => {
            const active = state.background_environment_id === e.id;
            const disabled = e.is_enabled === false;
            return (
              <div key={e.id} className={`pg-card overflow-hidden ${active ? 'ring-2 ring-[var(--pg-primary)]' : ''} ${disabled ? 'opacity-60' : ''}`} data-testid="world-env-card">
                <div className="aspect-[16/10] bg-[var(--pg-bg-2)] relative">
                  <img src={mediaUrl(e.image_url)} alt={e.name} className="absolute inset-0 w-full h-full object-cover" />
                  {active && <span className="absolute top-2 left-2 pg-onair">ACTIVE</span>}
                  {disabled && <span className="absolute top-2 right-2 text-[10px] font-mono tracking-widest text-[var(--pg-neon-pink)] bg-[rgba(0,0,0,0.7)] px-2 py-1 rounded">DISABLED</span>}
                  <div className="absolute bottom-2 right-2 flex gap-1">
                    <button onClick={() => moveEnv(ei, -1)} disabled={ei === 0} className="h-7 w-7 rounded-md bg-[rgba(0,0,0,0.6)] backdrop-blur text-[var(--pg-ink-muted)] hover:text-[var(--pg-ink)] disabled:opacity-30 flex items-center justify-center" aria-label="Move up" data-testid="env-move-up">↑</button>
                    <button onClick={() => moveEnv(ei, 1)} disabled={ei === envs.length - 1} className="h-7 w-7 rounded-md bg-[rgba(0,0,0,0.6)] backdrop-blur text-[var(--pg-ink-muted)] hover:text-[var(--pg-ink)] disabled:opacity-30 flex items-center justify-center" aria-label="Move down" data-testid="env-move-down">↓</button>
                  </div>
                </div>
                <div className="p-3">
                  <p className="font-serif text-base text-[var(--pg-ink)]">{e.name}</p>
                  {e.description && <p className="text-xs text-[var(--pg-ink-muted)] mt-1 line-clamp-2">{e.description}</p>}
                  <div className="mt-2 flex items-center gap-1">
                    {['primary','secondary','tertiary','accent','deep'].map((k) => (
                      <span key={k} title={k} className="w-4 h-4 rounded-full border border-[var(--pg-border-2)]" style={{ background: e.palette?.[k] }} />
                    ))}
                  </div>
                  {(e.scheduled_from || e.scheduled_to) && (
                    <p className="text-[10px] font-mono text-[var(--pg-gold-2)] mt-2 tracking-widest">
                      ⏱ {e.scheduled_from ? `from ${(e.scheduled_from || '').slice(0,10)}` : ''}
                      {e.scheduled_to ? ` → ${(e.scheduled_to || '').slice(0,10)}` : ''}
                    </p>
                  )}
                  <div className="mt-3 flex items-center gap-2">
                    <button disabled={active} onClick={() => selectEnv(e)} className={`pg-btn-primary !py-1.5 !text-[11px] flex-1 justify-center ${active ? 'opacity-50' : ''}`} data-testid="env-select">{active ? 'ACTIVE' : 'SELECT'}</button>
                    <button onClick={() => patchEnv(e.id, { is_enabled: !disabled })} className="pg-btn-ghost !py-1.5 !text-[11px] !px-3" data-testid="env-toggle-enabled">{disabled ? 'ENABLE' : 'DISABLE'}</button>
                    <button onClick={() => deleteEnv(e.id)} className="text-[var(--pg-primary)] text-xs font-mono px-2" data-testid="env-delete">✕</button>
                  </div>
                  <details className="mt-3 group">
                    <summary className="cursor-pointer text-[10px] font-mono tracking-[0.24em] text-[var(--pg-ink-muted)] uppercase hover:text-[var(--pg-ink)] list-none flex items-center gap-1">
                      <span className="group-open:rotate-90 inline-block transition-transform">▸</span> SCHEDULE + ANIMATION
                    </summary>
                    <div className="mt-2 space-y-2 pl-1">
                      <div className="grid grid-cols-2 gap-2">
                        <label className="text-[10px] font-mono text-[var(--pg-ink-muted)]">
                          From
                          <input type="datetime-local" defaultValue={(e.scheduled_from || '').slice(0,16)} onBlur={(ev) => patchEnv(e.id, { scheduled_from: ev.target.value ? new Date(ev.target.value).toISOString() : null })} className="mt-0.5 w-full pg-input rounded-[var(--pg-radius-sm)] px-2 py-1 text-[11px]" data-testid="env-scheduled-from" />
                        </label>
                        <label className="text-[10px] font-mono text-[var(--pg-ink-muted)]">
                          To
                          <input type="datetime-local" defaultValue={(e.scheduled_to || '').slice(0,16)} onBlur={(ev) => patchEnv(e.id, { scheduled_to: ev.target.value ? new Date(ev.target.value).toISOString() : null })} className="mt-0.5 w-full pg-input rounded-[var(--pg-radius-sm)] px-2 py-1 text-[11px]" data-testid="env-scheduled-to" />
                        </label>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <label className="text-[10px] font-mono text-[var(--pg-ink-muted)]">
                          Anim. intensity ({e.animation_intensity_override ?? 'global'})
                          <input type="range" min={0} max={1} step={0.05} defaultValue={e.animation_intensity_override ?? state.animation_intensity} onMouseUp={(ev) => patchEnv(e.id, { animation_intensity_override: parseFloat(ev.target.value) })} className="mt-1 w-full accent-[var(--pg-emerald)]" data-testid="env-anim-override" />
                        </label>
                        <label className="text-[10px] font-mono text-[var(--pg-ink-muted)] flex items-center gap-2 pt-3">
                          <input type="checkbox" defaultChecked={e.motion_enabled_override ?? true} onChange={(ev) => patchEnv(e.id, { motion_enabled_override: ev.target.checked })} className="accent-[var(--pg-emerald)]" data-testid="env-motion-override" />
                          Motion (this env)
                        </label>
                      </div>
                      <button onClick={() => patchEnv(e.id, { animation_intensity_override: null, motion_enabled_override: null, overlay_strength_override: null })} className="text-[10px] font-mono tracking-[0.24em] text-[var(--pg-ink-faint)] hover:text-[var(--pg-ink)] uppercase">Reset to global</button>
                    </div>
                  </details>
                </div>
              </div>
            );
          })}
          {/* Upload card */}
          <form onSubmit={onAddEnv} className="pg-card p-4 flex flex-col gap-2">
            <p className="pg-label">UPLOAD NEW ENVIRONMENT</p>
            <input value={newEnv.name} onChange={(e) => setNewEnv({ ...newEnv, name: e.target.value })} placeholder="Environment name" className="pg-input rounded-[var(--pg-radius-sm)] px-3 py-2 text-sm" />
            <textarea value={newEnv.description} onChange={(e) => setNewEnv({ ...newEnv, description: e.target.value })} placeholder="One-line description" rows={2} className="pg-input rounded-[var(--pg-radius-sm)] px-3 py-2 text-sm" />
            <label className="pg-btn-ghost cursor-pointer text-[11px] justify-center">
              {uploading ? 'Uploading…' : newEnv.image_url ? '✓ Image attached (replace)' : 'Upload backdrop image'}
              <input type="file" accept="image/*" hidden onChange={async (e) => {
                const f = e.target.files?.[0]; if (!f) return;
                setUploading(true);
                try { const url = await uploadEnvImage(f); setNewEnv((n) => ({ ...n, image_url: url })); }
                catch (err) { alert(err?.response?.data?.detail || 'Upload failed'); }
                finally { setUploading(false); }
              }} />
            </label>
            <div className="grid grid-cols-5 gap-1">
              {['primary','secondary','tertiary','accent','deep'].map((k) => (
                <label key={k} className="flex flex-col gap-1 text-[9px] font-mono text-[var(--pg-ink-muted)]">
                  {k.slice(0,3).toUpperCase()}
                  <input type="color" value={newEnv.palette[k]} onChange={(e) => setNewEnv({ ...newEnv, palette: { ...newEnv.palette, [k]: e.target.value } })} className="w-full h-8 rounded cursor-pointer bg-transparent border border-[var(--pg-border-2)]" />
                </label>
              ))}
            </div>
            <button type="submit" className="pg-btn-emerald !py-2 !text-[11px] justify-center mt-1">Add environment</button>
          </form>
        </div>
      </div>

      {/* Animation + transition + atmosphere controls */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="pg-card p-6">
          <p className="pg-label">TRANSITION & MOTION</p>
          <h3 className="font-display text-xl mt-1 tracking-[0.04em]">HOW THE WORLD SHIFTS</h3>
          <div className="mt-5 grid grid-cols-2 gap-4">
            <Sel label="Transition style" k="transition_style" opts={[{value:'crossfade',label:'Crossfade (smooth)'},{value:'fade',label:'Fade'}]} />
            <div>
              <label className="text-xs font-mono tracking-widest text-[var(--pg-ink-muted)]">Transition duration ({state.transition_duration_ms}ms)</label>
              <input type="range" min="200" max="8000" step="100" value={state.transition_duration_ms} onChange={(e) => update('transition_duration_ms', parseInt(e.target.value || 0))} className="mt-2 w-full accent-[var(--pg-primary)]" />
              <div className="flex items-center justify-between mt-1">
                <span className="text-[10px] font-mono text-[var(--pg-ink-faint)] tracking-widest">FAST 200ms</span>
                <span className="text-[10px] font-mono text-[var(--pg-ink-faint)] tracking-widest">CINEMATIC 8000ms</span>
              </div>
              <p className="text-[10px] font-mono text-[var(--pg-ink-faint)] mt-1">Sweet spot: 1500–2500ms for balance. Always smooth crossfade.</p>
            </div>
            <div>
              <label className="text-xs font-mono tracking-widest text-[var(--pg-ink-muted)]">Rotation interval (min)</label>
              <input type="number" min="1" max="240" value={state.rotation_interval_minutes} onChange={(e) => update('rotation_interval_minutes', parseInt(e.target.value || 1))} className="mt-1 w-full pg-input rounded-[var(--pg-radius-sm)] px-3 py-2 text-sm" disabled={state.background_mode !== 'auto_rotate'} />
            </div>
            <label className="flex items-end gap-2 text-xs font-mono tracking-widest text-[var(--pg-ink-muted)] cursor-pointer">
              <input type="checkbox" checked={state.motion_enabled} onChange={(e) => update('motion_enabled', e.target.checked)} className="accent-[var(--pg-primary)]" /> Motion enabled
            </label>
          </div>
          <div className="mt-5 grid grid-cols-2 gap-4">
            <Num label="Animation intensity" k="animation_intensity" min={0} max={1} step={0.05} />
            <Num label="Overlay strength" k="overlay_strength" min={0} max={1} step={0.01} />
          </div>
        </div>

        <div className="pg-card p-6">
          <p className="pg-label">ATMOSPHERE</p>
          <h3 className="font-display text-xl mt-1 tracking-[0.04em]">TIME • WEATHER • MOOD</h3>
          <div className="mt-5 grid grid-cols-2 gap-4">
            <Sel label="Time" k="time_profile" opts={['automatic','sunrise','morning','afternoon','golden_hour','sunset','evening','night'].map((v) => ({ value: v, label: v.replace('_',' ') }))} />
            <Sel label="Weather" k="weather" opts={['automatic','clear','partly_cloudy','overcast','mist','rain','snow'].map((v) => ({ value: v, label: v.replace('_',' ') }))} />
            <Sel label="Mood" k="mood" opts={['calm','dream','reflect','inspire','focus','celebrate'].map((v) => ({ value: v, label: v }))} />
            <Sel label="Engine" k="enabled" opts={[{value:true,label:'Enabled'},{value:false,label:'Disabled'}]} />
          </div>
        </div>
      </div>

      <button onClick={save} disabled={saving} className="pg-btn-primary w-full justify-center !py-3 disabled:opacity-60">{saving ? 'Publishing…' : 'PUBLISH CHANGES'}</button>
      {msg && <p className="text-xs font-mono text-[var(--pg-tertiary)] text-center">{msg}</p>}
    </div>
  );
}

function CollabSubmissionsAdmin() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(null);

  const load = () => {
    setLoading(true);
    api.get('/admin/collab/submissions').then(({ data }) => setItems(data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const updateOne = async (id, patch) => {
    try { await api.put(`/admin/collab/submissions/${id}`, patch); load(); if (open?.id === id) setOpen({ ...open, ...patch }); }
    catch (e) { alert(e?.response?.data?.detail || 'Update failed'); }
  };

  const del = async (id) => {
    if (!window.confirm('Delete this submission?')) return;
    await api.delete(`/admin/collab/submissions/${id}`); setOpen(null); load();
  };

  const statusColor = (s) => ({ new: 'pg-text-glow-pink', in_review: 'pg-text-glow-gold', replied: 'pg-text-glow-emerald', archived: 'text-[var(--pg-ink-faint)]' })[s] || '';

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
      <div className="lg:col-span-5">
        <p className="text-sm text-[var(--pg-ink-muted)] font-mono mb-3">{loading ? 'Loading…' : `${items.length} submissions`}</p>
        <div className="pg-card overflow-hidden">
          {items.length === 0 ? <div className="p-10 text-center text-[var(--pg-ink-muted)]">No submissions yet.</div>
          : <ul className="divide-y divide-[var(--pg-border-2)] max-h-[65vh] overflow-y-auto pg-scroll">
            {items.map((s) => (
              <li key={s.id}>
                <button onClick={() => setOpen(s)} className={`w-full text-left p-4 hover:bg-[rgba(242,238,227,0.04)] ${open?.id === s.id ? 'bg-[rgba(226,199,122,0.05)]' : ''}`}>
                  <div className="flex items-center justify-between">
                    <span className="font-serif text-base text-[var(--pg-ink)] truncate">{s.subject || s.name}</span>
                    <span className={`text-[10px] font-mono tracking-widest uppercase ${statusColor(s.status)}`}>{s.status}</span>
                  </div>
                  <p className="text-xs text-[var(--pg-ink-muted)] mt-1 truncate">{s.name} · {s.category.replace(/_/g, ' ')}</p>
                  <p className="text-[10px] font-mono text-[var(--pg-ink-faint)] mt-1">{new Date(s.created_at).toLocaleString()}</p>
                </button>
              </li>
            ))}
          </ul>}
        </div>
      </div>
      <div className="lg:col-span-7">
        {open ? (
          <div className="pg-card p-6 sticky top-24">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-mono text-[10px] tracking-[0.32em] pg-text-glow-gold">{open.reference}</p>
                <h3 className="font-serif text-2xl mt-1">{open.subject}</h3>
                <p className="text-xs text-[var(--pg-ink-muted)] mt-1 capitalize">{open.category.replace(/_/g, ' ')}</p>
              </div>
              <select value={open.status} onChange={(e) => updateOne(open.id, { status: e.target.value })} className="pg-input rounded-full px-3 py-1.5 text-xs">
                <option value="new">New</option><option value="in_review">In review</option><option value="replied">Replied</option><option value="archived">Archived</option>
              </select>
            </div>
            <div className="pg-hairline my-4" />
            <p className="text-sm text-[var(--pg-ink)]">{open.name} <span className="text-[var(--pg-ink-muted)]">&lt;{open.email}&gt;</span></p>
            {open.organization && <p className="text-xs text-[var(--pg-ink-muted)] mt-1">{open.organization}</p>}
            <p className="text-sm text-[var(--pg-ink)] mt-4 whitespace-pre-wrap leading-relaxed">{open.message}</p>
            {open.file_url && <a href={mediaUrl(open.file_url)} target="_blank" rel="noreferrer" className="mt-4 inline-block pg-btn-ghost">📎 {open.file_name || 'attachment'}</a>}
            <div className="mt-5">
              <label className="text-xs font-mono tracking-widest text-[var(--pg-ink-muted)]">Internal notes</label>
              <textarea rows={3} defaultValue={open.notes || ''} onBlur={(e) => updateOne(open.id, { notes: e.target.value })} className="mt-1 w-full pg-input rounded-[var(--pg-radius-sm)] px-3 py-2 text-sm" />
            </div>
            <div className="mt-5 flex justify-between items-center">
              <a href={`mailto:${open.email}?subject=Re:%20${encodeURIComponent(open.subject)}`} className="pg-btn-gold !py-2 !text-xs">Reply by email</a>
              <button onClick={() => del(open.id)} className="text-xs font-mono tracking-widest text-[var(--pg-neon-pink)] hover:opacity-80">Delete</button>
            </div>
          </div>
        ) : (
          <div className="pg-card p-10 text-center text-[var(--pg-ink-muted)]">Select a submission to view details.</div>
        )}
      </div>
    </div>
  );
}

// Named exports so shared admin components (e.g. MixedChannelAdmin) can reuse
// the same Drawer + per-content-type form renderers. All are hoisted function
// declarations defined earlier in this module.
export {
  Drawer,
  AudioForm,
  VideoForm,
  ArticleForm,
  ProductForm,
  GalleryForm,
  ExternalLinkForm,
  CONTENT_TYPE_CONFIG,
  defaults as ADMIN_DEFAULTS,
};

